
import { useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { format, isPast } from "date-fns";
import { pt, enUS } from "date-fns/locale";
import { toast } from "sonner";
import { StudentEvent } from "./types";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Globe } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";

interface RequestScheduleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  date: Date;
  onSuccess?: (event: StudentEvent) => void;
}

export const RequestScheduleDialog = ({
  isOpen,
  onClose,
  date,
  onSuccess,
}: RequestScheduleDialogProps) => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  const { t } = useTranslations("schedule");
  const dateLocale = language === 'pt' ? pt : enUS;
  
  // Verificar se a data é no passado
  const isDateInPast = isPast(date);

  const handleRequest = async () => {
    if (!profile?.id) {
      toast.error(language === 'pt' ? 'Utilizador não autenticado' : 'User not authenticated');
      return;
    }
    
    // Impedir solicitações para datas passadas
    if (isDateInPast) {
      toast.error(t("eventDialog.pastDateError"));
      return;
    }

    setLoading(true);
    try {
      console.log('Fetching student data for auth_id:', profile.id);
      
      const { data: student, error: studentError } = await supabase
        .from('students')
        .select('id, trainer_id')
        .eq('auth_id', profile.id)
        .maybeSingle();

      if (studentError) {
        console.error('Error fetching student:', studentError);
        toast.error(language === 'pt' ? 'Erro ao encontrar informações do aluno' : 'Error finding student information');
        return;
      }

      if (!student) {
        console.error('No student found for auth_id:', profile.id);
        toast.error(language === 'pt' ? 'Aluno não encontrado' : 'Student not found');
        return;
      }

      if (!student.trainer_id) {
        console.error('No trainer_id found for student:', student.id);
        toast.error(language === 'pt' ? 'Personal trainer não encontrado' : 'Personal trainer not found');
        return;
      }

      // 1. Create the schedule request
      const { data: requestData, error: requestError } = await supabase
        .from('schedule_requests')
        .insert({
          student_id: student.id,
          trainer_id: student.trainer_id,
          requested_date: date.toISOString(),
          status: 'pending',
          request_online: isOnline
        })
        .select()
        .single();

      if (requestError) {
        console.error('Error creating schedule request:', requestError);
        throw requestError;
      }

      console.log('Schedule request created successfully:', requestData);

      // 2. Create an event in the trainer's calendar with status 'scheduled'
      // This event will be visible to all students as "Ocupado"
      const eventEndTime = new Date(new Date(date).getTime() + 60 * 60 * 1000);
      
      // Create the event with proper schema structure and explicit type casting
      const scheduleEventData = {
        title: language === 'pt' ? 'Ocupado' : 'Occupied',
        start_time: date.toISOString(),
        end_time: eventEndTime.toISOString(),
        event_type: 'training_session' as const, // Use as const to satisfy TypeScript
        status: 'scheduled' as const, // Use scheduled status to make it visible to all students
        related_id: requestData.id,
        student_id: student.id,
        trainer_id: student.trainer_id,
        description: language === 'pt' ? 'Pedido de aula pendente' : 'Pending class request',
        is_online: isOnline
      };
      
      console.log('Creating schedule event with data:', scheduleEventData);
      
      const { data: eventData, error: eventError } = await supabase
        .from('schedule_events')
        .insert(scheduleEventData)
        .select()
        .single();
        
      if (eventError) {
        console.error('Error creating event:', eventError);
        console.error('Event data that failed:', scheduleEventData);
        // Continue anyway since the request was created
      } else {
        console.log('Event created successfully:', eventData);
      }

      if (requestData) {
        const newEvent: StudentEvent = {
          id: requestData.id,
          title: isOnline ? 
            (language === 'pt' ? 'Pedido de Aula Online' : 'Online Class Request') :
            (language === 'pt' ? 'Pedido de Aula' : 'Class Request'),
          start_time: requestData.requested_date,
          end_time: eventEndTime.toISOString(),
          event_type: isOnline ? 'online_session' : 'training_session',
          student_id: requestData.student_id,
          trainer_id: requestData.trainer_id,
          status: requestData.status,
          type: 'request',
          studentName: null,
          isCurrentStudent: true,
          related_id: eventData?.id,
          is_online: isOnline
        };
        
        if (onSuccess) {
          onSuccess(newEvent);
        }

        toast.success(language === 'pt' ? 'Pedido enviado com sucesso' : 'Request sent successfully');
        onClose();
      }

      // Invalidate related queries to trigger a refresh
      queryClient.invalidateQueries();
    } catch (error) {
      console.error('Error requesting schedule:', error);
      toast.error(language === 'pt' ? 'Erro ao enviar pedido' : 'Error sending request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${isMobile ? 'w-[90%] max-w-[350px]' : 'sm:max-w-md'}`}>
        <DialogHeader>
          <DialogTitle>{t("eventDialog.requestSchedule")}</DialogTitle>
          <DialogDescription>
            {t("eventDialog.confirmTime")}:
            {format(date, "dd 'de' MMMM 'às' HH:mm", { locale: dateLocale })}
          </DialogDescription>
        </DialogHeader>

        {isDateInPast && (
          <Alert variant="warning" className="my-3">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {t("eventDialog.cantRequestPast")}
            </AlertDescription>
          </Alert>
        )}
        
        <div className="flex items-center space-x-2 my-4">
          <Switch
            id="isOnline"
            checked={isOnline}
            onCheckedChange={setIsOnline}
          />
          <Label htmlFor="isOnline" className="flex items-center gap-2">
            {language === 'pt' ? 'Solicitar aula online' : 'Request online class'}
            {isOnline && <Globe className="h-4 w-4 text-blue-500" />}
          </Label>
        </div>

        <div className="flex justify-end gap-4 mt-4">
          <Button variant="outline" onClick={onClose}>
            {t("eventDialog.cancelRequest")}
          </Button>
          <Button 
            onClick={handleRequest} 
            disabled={loading || isDateInPast}
          >
            {loading ? t("eventDialog.sending") : t("eventDialog.confirmRequest")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
